import { ColorPaletteSelector } from '@/components/shared/color-palette-selector';
import { MutedText } from '@/components/ui/typography';
import { useDebounce } from '@/hooks/use-debounce';
import { readString } from '@/lib/prop-utils';
import { resolveDisplayColor } from '@/lib/site-design/colors';
import { useSiteStylesStore } from '@/stores/site-styles-store';

const FIELDS = [
  {
    key: 'borderColor' as const,
    label: 'Border Color',
    description: 'Set a custom border color for cards (overrides site-wide colors)',
  },
  {
    key: 'backgroundColor' as const,
    label: 'Background Color',
    description: 'Set a custom background color for cards (overrides site-wide colors)',
  },
] as const;

type BackgroundKey = (typeof FIELDS)[number]['key'];

export function BackgroundTab({
  variantProps,
  onVariantPropsChange,
}: {
  variantProps: Record<string, unknown>;
  onVariantPropsChange: (patch: Record<string, unknown>) => void;
}) {
  const siteColors = useSiteStylesStore((s) => s.draft.colors);
  const debouncedChange = useDebounce(onVariantPropsChange, 150);
  const values: Record<BackgroundKey, string> = {
    borderColor: resolveDisplayColor(
      readString(variantProps.borderColor),
      siteColors,
      siteColors?.borders,
    ),
    backgroundColor: resolveDisplayColor(
      readString(variantProps.backgroundColor),
      siteColors,
      siteColors?.cardBackground,
    ),
  };

  return (
    <div className="space-y-2">
      {FIELDS.map(({ key, label, description }) => (
        <div key={key}>
          <ColorPaletteSelector<BackgroundKey>
            fields={[{ key, label }]}
            values={{ [key]: values[key] } as Record<BackgroundKey, string>}
            onChange={(k, value) => debouncedChange({ [k]: value })}
          />
          <MutedText className="text-xs">{description}</MutedText>
        </div>
      ))}
    </div>
  );
}
